import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { inr } from "../lib/api";
import { useCart } from "../context/CartContext";

const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, subtotal, count } = useCart();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            key="panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 z-[90] h-full w-full max-w-md bg-[#101013] border-l border-[#d4af37]/20 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#d4af37]/15">
              <p className="font-serif text-2xl text-zinc-100">
                Your Bag <span className="text-[#d4af37] text-lg">({count})</span>
              </p>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-close-button"
                className="p-2 rounded-full text-zinc-400 hover:text-[#f3e5ab] hover:bg-[#d4af37]/10 transition-all"
                aria-label="Close cart"
              >
                <X size={20} strokeWidth={1.6} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-8" data-testid="cart-empty-state">
                <span className="p-5 rounded-full border border-[#d4af37]/25 text-[#d4af37]">
                  <ShoppingBag size={28} strokeWidth={1.2} />
                </span>
                <p className="font-serif text-2xl text-zinc-200">Your bag awaits its first sparkle</p>
                <p className="text-sm text-zinc-500">Explore hand-finished pieces crafted in 18k gold plating.</p>
                <Link to="/shop" onClick={() => setOpen(false)} className="btn-gold mt-2" data-testid="cart-empty-shop-button">
                  Shop the Collection
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {items.map((i) => (
                    <div key={i.product_id} className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
                      <img src={i.image} alt={i.name} className="w-20 h-24 object-cover rounded-lg border border-[#d4af37]/15" />
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-lg text-zinc-100 leading-snug">{i.name}</p>
                        <p className="text-sm text-zinc-400 mt-0.5">{inr(i.price)}</p>
                        <div className="mt-2 flex items-center gap-3">
                          <div className="flex items-center border border-[#d4af37]/25 rounded-full">
                            <button onClick={() => setQty(i.product_id, i.qty - 1)} className="px-2.5 py-1.5 text-zinc-400 hover:text-[#f3e5ab]" data-testid={`cart-decrement-${i.product_id}`} aria-label="Decrease quantity">
                              <Minus size={13} />
                            </button>
                            <span className="px-2 text-sm text-zinc-200 min-w-[20px] text-center" data-testid={`cart-qty-${i.product_id}`}>{i.qty}</span>
                            <button onClick={() => setQty(i.product_id, i.qty + 1)} className="px-2.5 py-1.5 text-zinc-400 hover:text-[#f3e5ab]" data-testid={`cart-increment-${i.product_id}`} aria-label="Increase quantity">
                              <Plus size={13} />
                            </button>
                          </div>
                          <button
                            onClick={() => remove(i.product_id)}
                            data-testid={`cart-remove-${i.product_id}`}
                            className="text-xs tracking-wide uppercase text-zinc-500 hover:text-red-400 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-zinc-300 font-medium">{inr(i.price * i.qty)}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-[#d4af37]/15 px-6 py-5 space-y-4 bg-[#0c0c0e]">
                  <p className="text-xs text-[#d4af37]/80 tracking-wide">
                    Use code <span className="font-semibold text-[#f3e5ab]">AURELIA10</span> at checkout for 10% off.
                  </p>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Subtotal</span>
                    <span className="text-zinc-100 font-medium" data-testid="cart-subtotal">{inr(subtotal)}</span>
                  </div>
                  <Link to="/checkout" onClick={() => setOpen(false)} className="btn-gold w-full" data-testid="cart-checkout-button">
                    Proceed to Checkout <ArrowRight size={16} />
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
